import { useCallback, useState } from 'react';

import { cx } from '@libs/classnames';
import { CapsuleButton } from '@ui/components/CapsuleButton';
import { DropdownWithPopover } from '@ui/components/DropdownWithPopover/DropdownWithPopover';
import { IconButton } from '@ui/components/IconButton';
import { Loader } from '@ui/components/Loader';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { Placeholder } from '@ui/components/Placeholder/Placeholder';
import { SearchInput } from '@ui/components/SearchInput';
import { FilterIcon } from '@ui/icons/FilterIcon';

import { TournamentsListItem } from '@features/club/ui/TournamentsList/TournamentsListItem';
import { components } from '@features/shared/api/public';
import { ClockControlType, TournamentMode } from '@features/shared/api/typings';

import styles from './TournamentsList.module.css';

const CLOCK_CONTROL_OPTIONS: { label: string; value?: ClockControlType }[] = [
  { label: 'Любой', value: undefined },
  { label: 'Пуля', value: 'Bullet' },
  { label: 'Блиц', value: 'Blitz' },
  { label: 'Рапид', value: 'Rapid' },
];

const MODE_OPTIONS: { label: string; value?: TournamentMode }[] = [
  { label: 'Любой', value: undefined },
  { label: 'Личный', value: 'Private' },
  { label: 'Командный', value: 'Team' },
];

type Props = {
  bulletEnabled: boolean;
  tournaments?: components['schemas']['TournamentPublicDto'][];
  query?: string;
  clockControl?: ClockControlType;
  onClockControlChange: (newValue?: ClockControlType) => void;
  mode?: TournamentMode;
  onModeChange: (newValue?: TournamentMode) => void;
  onQueryChange: (newQuery: string) => void;
  onLoadMore?: () => void;
  isLoading: boolean;
};

export function TournamentsList({
  bulletEnabled,
  tournaments,
  query,
  onQueryChange,
  clockControl,
  onClockControlChange,
  mode,
  onModeChange,
  onLoadMore,
  isLoading,
}: Props) {
  let clockControlOptions = CLOCK_CONTROL_OPTIONS;
  if (!bulletEnabled) {
    clockControlOptions = clockControlOptions.filter((o) => o.value !== 'Bullet');
  }

  const [isSearchCollapsed, setIsSearchCollapsed] = useState(true);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const toggleFilters = useCallback(() => {
    if (isFiltersVisible) {
      onClockControlChange();
      onModeChange();
    }

    setIsFiltersVisible(!isFiltersVisible);
  }, [setIsFiltersVisible, isFiltersVisible, onClockControlChange, onModeChange]);
  const isMobile = useMediaQuery() === 'mobile';

  if (!tournaments) return <Loader />;

  return (
    <div className={styles.tournamentsListWrapper}>
      <div className={styles.header}>
        <div
          className={cx(styles.title, {
            [styles.collapsed]: !isSearchCollapsed,
          })}
        >
          Все турниры
        </div>

        <div className={styles.search}>
          {!isMobile && (
            <>
              <DropdownWithPopover
                options={clockControlOptions}
                onChange={onClockControlChange}
                value={clockControl}
                valuePrefix="Контроль: "
                flat
              />

              <DropdownWithPopover
                options={MODE_OPTIONS}
                onChange={onModeChange}
                value={mode}
                valuePrefix="Формат: "
                flat
              />
            </>
          )}

          {isMobile && (
            <IconButton
              className={cx(styles.filtersButton, {
                [styles.active]: isFiltersVisible,
                [styles.collapsed]: !isSearchCollapsed,
              })}
              onClick={toggleFilters}
            >
              <FilterIcon />
            </IconButton>
          )}

          <SearchInput
            collapsible={isMobile}
            onCollapseChange={setIsSearchCollapsed}
            onQueryChange={onQueryChange}
            query={query}
            placeholder="Поиск по названию"
          />
        </div>
      </div>

      {isMobile && (
        <div
          className={cx(styles.mobileFiltersBar, {
            [styles.visible]: isFiltersVisible,
          })}
        >
          <DropdownWithPopover
            options={CLOCK_CONTROL_OPTIONS}
            onChange={onClockControlChange}
            value={clockControl}
            valuePrefix="Контроль: "
            flat
          />

          <DropdownWithPopover
            options={MODE_OPTIONS}
            onChange={onModeChange}
            value={mode}
            valuePrefix="Формат: "
            flat
          />
        </div>
      )}

      <div className={styles.tournamentsList}>
        <div className={styles.listHeader}>
          <div className={styles.tournamentName}>Название турнира</div>
          <div className={styles.tournamentOrganizer}>Организатор</div>
          <div className={cx(styles.tournamentInfo, styles.wider)}>
            <div className={styles.tournamentClockControl}>Контроль времени</div>
            <div className={styles.tournamentEntrance}>Формат турнира</div>
          </div>
          <div className={styles.tournamentInfo}>
            <div className={styles.tournamentParticipants}>Участники</div>
            <div className={styles.tournamentDate}>Дата</div>
          </div>
        </div>

        {tournaments.map((tournament) => (
          <TournamentsListItem key={tournament.tournamentId} tournament={tournament} />
        ))}

        {onLoadMore && (
          <div className={styles.loadMoreButton}>
            <CapsuleButton preset="gray" size="16m-40" onClick={onLoadMore}>
              Показать еще
            </CapsuleButton>
          </div>
        )}

        {isLoading && <Loader centered />}

        {!isLoading && tournaments && !tournaments.length && (
          <Placeholder>Нет турниров</Placeholder>
        )}
      </div>
    </div>
  );
}
